import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import {
  reports,
  type ReportHelp,
  type ReportMeasure,
  type ReportPage,
  type ReportPageKey,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { cn } from '@/shared/lib/cn';
import { formatDateTime } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Modal } from '@/shared/ui/Modal';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Skeleton } from '@/shared/ui/Skeleton';

import { Detalhamento } from './Detalhamento';
import { lerFiltro } from './filtro';
import { PainelDeFiltros } from './PainelDeFiltros';
import { VisualCard } from './Visual';

const PAGINAS: ReportPageKey[] = [
  'resumo',
  'cotacoes',
  'negociacao',
  'esteira',
  'apolices',
  'endossos',
  'tomadores',
  'carteira',
  'acumulo',
  'corretoras',
  'recorrencia',
  'consultas',
  'aprovacao',
];

/** Visuais largos (matriz, tabela) ocupam a linha inteira; os demais, meia. */
const largos = new Set(['matriz', 'tabela', 'cartao_varias_linhas']);

/**
 * Relatorios: o BI do OnPoint ("OnPoint | Resultado Operacional") construido no
 * SeguraCore, sem ferramenta externa (ADR 0043). Uma aba por pagina, na ordem do
 * navegador de paginas do OnPoint, com o endereco da pagina na URL (/bi/:pageKey).
 *
 * A pagina que dependa de outro pacote aparece com o motivo. Cada numero tem a
 * definicao escrita em "Definicoes dos indicadores"; o que nao tem definicao aprovada
 * aparece como "pendente de definicao", e nunca como zero. As paginas do P-BI-2 tem
 * segmentacoes proprias, botoes de ajuda e, na Esteira, no Acumulo e na Aprovacao,
 * duas tabelas de registros.
 */
export function ReportsPage() {
  const { can, session } = useAuth();
  // A corretora nao ve as paginas da seguradora (o servidor responde 403): a aba nao
  // aparece e a pagina aberta pelo endereco diz por que, sem consultar.
  const daCorretora = session?.broker_scoped === true;
  const parametros = useParams<{ pageKey?: string }>();
  const [busca, setBusca] = useSearchParams();
  const [definicoesAbertas, setDefinicoesAbertas] = useState(false);
  const [ajudaAberta, setAjudaAberta] = useState<ReportHelp | null>(null);

  const podeVer = can('bi.painel.view');
  const chave = (parametros.pageKey ?? 'resumo') as ReportPageKey;
  const conhecida = PAGINAS.includes(chave);
  const filtro = lerFiltro(busca);

  const catalogo = useQuery({
    queryKey: ['bi', 'paginas'],
    queryFn: ({ signal }) => reports.list(signal),
    enabled: podeVer,
  });
  const paginaAtual = catalogo.data?.find((item) => item.key === chave);
  const daSeguradora = daCorretora && paginaAtual?.insurer_only === true;
  const pagina = useQuery({
    queryKey: ['bi', 'pagina', chave, busca.toString()],
    queryFn: ({ signal }) => reports.page(chave, filtro, signal),
    // Com escopo de corretora, espera o catalogo dizer se a pagina e da seguradora.
    enabled:
      podeVer &&
      conhecida &&
      (!daCorretora || catalogo.data !== undefined) &&
      !daSeguradora,
  });

  if (!podeVer) {
    return (
      <div className="flex flex-col gap-5">
        <PageHeader title="Relatorios" />
        <Alert tone="info" title="Relatorios indisponiveis para o seu acesso">
          O seu perfil nao inclui a visualizacao dos relatorios.
        </Alert>
      </div>
    );
  }

  const dados = pagina.data;
  const visiveis = daCorretora
    ? catalogo.data?.filter((item) => item.insurer_only !== true)
    : catalogo.data;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Relatorios"
        description="Resultado operacional da empresa: cotacoes, apolices, esteiras, tomadores, carteira e corretoras, calculados no SeguraCore."
        actions={
          dados ? (
            <div className="flex flex-col items-end gap-1">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setDefinicoesAbertas(true)}
              >
                Definicoes dos indicadores
              </Button>
              <span className="text-2xs text-ink-500">
                Atualizado em {formatDateTime(dados.generated_at)}
              </span>
            </div>
          ) : null
        }
      />

      <Abas paginas={visiveis} atual={chave} busca={busca.toString()} />

      {!conhecida ? (
        <Alert tone="warning" title="Pagina de relatorio desconhecida">
          Escolha uma das abas acima.
        </Alert>
      ) : null}

      {conhecida && paginaAtual?.availability === 'pendente' ? (
        <Alert tone="info" title={`${paginaAtual.title}: pagina pendente`}>
          {paginaAtual.pending_reason}
        </Alert>
      ) : null}

      {daSeguradora ? (
        <Alert
          tone="info"
          title={`${paginaAtual?.title ?? 'Pagina'}: pagina da seguradora`}
        >
          A esteira, o grupo economico e o motor de credito sao da seguradora e nao
          valem para o portal da corretora.
        </Alert>
      ) : null}

      {conhecida && !daSeguradora && paginaAtual?.availability !== 'pendente' ? (
        <>
          <PainelDeFiltros pagina={dados} busca={busca} onChange={setBusca} />

          {pagina.isError ? (
            <Alert tone="danger" title="Nao foi possivel carregar a pagina">
              {pagina.error.message}
            </Alert>
          ) : null}

          {dados?.notices.map((aviso) => (
            <Alert key={aviso} tone="info">
              {aviso}
            </Alert>
          ))}

          {(dados?.help ?? []).length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {dados?.help?.map((ajuda) => (
                <Button
                  key={ajuda.key}
                  size="sm"
                  variant="secondary"
                  onClick={() => setAjudaAberta(ajuda)}
                >
                  {ajuda.title}
                </Button>
              ))}
            </div>
          ) : null}

          {pagina.isPending ? (
            <div className="grid gap-4 lg:grid-cols-2">
              {Array.from({ length: 4 }, (_, indice) => (
                <Skeleton key={indice} className="h-48 w-full" />
              ))}
            </div>
          ) : null}

          {dados?.sections.map((secao) => (
            <section key={secao.key} aria-labelledby={`secao-${secao.key}`}>
              <h2
                id={`secao-${secao.key}`}
                className="mb-3 rounded-xl bg-brand-900 bg-linear-to-r from-brand-900 via-brand-800 to-brand-700 px-4 py-2.5 text-sm font-semibold text-white shadow-(--shadow-sm)"
              >
                {secao.title}
              </h2>
              <div className="grid gap-4 lg:grid-cols-2">
                {secao.visuals.map((visual) => (
                  <div
                    key={visual.key}
                    className={cn(largos.has(visual.kind) && 'lg:col-span-2')}
                  >
                    <VisualCard visual={visual} />
                  </div>
                ))}
              </div>
            </section>
          ))}

          {dados && (dados.details?.length ?? 0) > 1
            ? dados.details?.map((detalhe) => (
                <Detalhamento
                  key={`${detalhe.key}-${busca.toString()}`}
                  pagina={chave}
                  filtro={filtro}
                  detalhe={detalhe}
                />
              ))
            : null}
          {dados && (dados.details?.length ?? 0) <= 1 ? (
            <Detalhamento key={busca.toString()} pagina={chave} filtro={filtro} />
          ) : null}

          <Definicoes
            aberto={definicoesAbertas}
            medidas={dados?.measures ?? []}
            onClose={() => setDefinicoesAbertas(false)}
          />
          <Ajuda ajuda={ajudaAberta} onClose={() => setAjudaAberta(null)} />
        </>
      ) : null}
    </div>
  );
}

/**
 * Texto de ajuda da pagina (paginas ocultas "Instrucao SLA" e "Instrucao Base de
 * Dados" do OnPoint): o que o OnPoint dizia, como o SeguraCore apura hoje e o que
 * esta pendente.
 */
function Ajuda({ ajuda, onClose }: { ajuda: ReportHelp | null; onClose: () => void }) {
  return (
    <Modal
      open={ajuda !== null}
      onClose={onClose}
      title={ajuda?.title ?? ''}
      description="Texto de ajuda da pagina. A regra descrita ainda nao foi homologada pela seguradora."
      size="lg"
    >
      <div className="flex flex-col gap-3">
        {ajuda?.paragraphs.map((paragrafo) => (
          <p key={paragrafo} className="text-sm text-ink-700">
            {paragrafo}
          </p>
        ))}
        {ajuda?.pending_reason ? (
          <p className="text-xs text-warning-700">Pendente: {ajuda.pending_reason}</p>
        ) : null}
      </div>
    </Modal>
  );
}

/** Abas das 13 paginas, com o filtro mantido de uma para a outra. */
function Abas({
  paginas,
  atual,
  busca,
}: {
  paginas: ReportPage[] | undefined;
  atual: ReportPageKey;
  busca: string;
}) {
  if (!paginas) {
    return <Skeleton className="h-10 w-full" />;
  }
  return (
    <nav
      aria-label="Paginas do relatorio"
      className="overflow-x-auto border-b border-line"
    >
      <ul className="flex min-w-max gap-1">
        {paginas.map((item) => {
          const ativa = item.key === atual;
          return (
            <li key={item.key}>
              <Link
                to={`/bi/${item.key}${busca ? `?${busca}` : ''}`}
                aria-current={ativa ? 'page' : undefined}
                className={cn(
                  'flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm',
                  ativa
                    ? 'border-brand-500 font-semibold text-brand-700'
                    : 'border-transparent text-ink-500 hover:border-line-strong hover:text-ink-900',
                )}
              >
                {item.title}
                {item.availability === 'pendente' ? (
                  <Badge tone="neutral">pendente</Badge>
                ) : null}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/**
 * As definicoes escritas das medidas da pagina. Nenhuma formula do OnPoint tem fonte
 * aprovada: toda medida aparece "nao homologada", a implementada com a definicao do
 * SeguraCore e a pendente com o motivo.
 */
function Definicoes({
  aberto,
  medidas,
  onClose,
}: {
  aberto: boolean;
  medidas: ReportMeasure[];
  onClose: () => void;
}) {
  return (
    <Modal
      open={aberto}
      onClose={onClose}
      title="Definicoes dos indicadores"
      description="O que cada numero desta pagina conta. Nenhuma definicao foi homologada pela seguradora ainda."
      size="lg"
    >
      <ul className="flex flex-col divide-y divide-line">
        {medidas.map((medida) => (
          <li key={medida.key} className="flex flex-col gap-1 py-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-ink-900">{medida.label}</span>
              <Badge tone={medida.status === 'implementada' ? 'success' : 'warning'}>
                {medida.status === 'implementada' ? 'Implementada' : 'Pendente'}
              </Badge>
              <Badge tone="neutral">
                {medida.homologation === 'homologada' ? 'Homologada' : 'Nao homologada'}
              </Badge>
            </div>
            {medida.matrix_ids ? (
              <p className="text-2xs text-ink-500">Referencia: {medida.matrix_ids}</p>
            ) : null}
            <p className="text-xs text-ink-700">{medida.definition}</p>
            {medida.pending_reason ? (
              <p className="text-xs text-warning-700">
                Pendente: {medida.pending_reason}
              </p>
            ) : null}
          </li>
        ))}
      </ul>
    </Modal>
  );
}
